// @flow

import {useEffect, useState} from 'react';
import {useCookies} from 'docs/data';
import type {Account} from 'docs/data';

// This represents a subset of the AnalyticsConfig type from Analytics.js.
export type StripeAnalyticsConfig = {
  ga?: {isLoggedIn?: boolean},
  locale?: string,
  merchant?: string,
  user?: string,
};

export type StripeAnalyticsClient = {
  configure: (config: StripeAnalyticsConfig) => Promise<void>,
  viewed: (name: string, properties?: Object) => Object,
  action: (name: string, properties?: Object) => Object,
  flushBufferedEvents?: (realClient: StripeAnalyticsClient) => void,
};

// This client is used on the server, and also during the initial load on the client
// before the configure() returns. It just buffers events, and then if/when we load
// the real client, we flush them by calling flushBufferedEvents().
const createBufferingClient = () => {
  const bufferedEvents = [];

  return {
    configure: () => Promise.resolve(),
    viewed: (name: string, properties: Object = {}) => {
      bufferedEvents.push({type: 'viewed', name, properties});
      return {};
    },
    action: (name: string, properties: Object = {}) => {
      bufferedEvents.push({type: 'action', name, properties});
      return {};
    },
    flushBufferedEvents: (realClient: StripeAnalyticsClient) => {
      bufferedEvents.forEach(({type, name, properties}) => {
        if (type === 'viewed') {
          realClient.viewed(name, properties);
        } else {
          realClient.action(name, properties);
        }
      });
    },
  };
};

// This client is used in the devbox environment to log analytics events to
// the console rather than actually sending them.
const createDevBoxClient = (): StripeAnalyticsClient => {
  let options = {};

  return {
    configure: async (config: StripeAnalyticsConfig) => {
      options = config;
    },
    viewed: (name: string, properties: Object = {}) => {
      // eslint-disable-next-line no-console
      console.log(`[Analytics] viewed`, {...options, ...properties});
      return {};
    },
    action: (name: string, properties: Object = {}) => {
      // eslint-disable-next-line no-console
      console.log(`[Analytics] action ${name}`, {...options, ...properties});
      return {};
    },
  };
};

// This client does nothing; it's only used if analytics.js fails to load.
const createNoopClient = (): StripeAnalyticsClient => ({
  configure: () => Promise.resolve(),
  viewed: (name: string, properties: Object = {}) => ({}),
  action: (name: string, properties: Object = {}) => ({}),
});

export const useStripeAnalyticsClient = (account: Account) => {
  const {cookies} = useCookies();

  // We can't start actually sending analytics events until we configure Analytics.js,
  // so we start out by creating a "buffering client" which just queues events.
  const [client, setClient] = useState<StripeAnalyticsClient>(
    createBufferingClient(),
  );

  // When the app spins up in the browser, we swap out the buffering client for a real
  // implementation. Typically that's Analytics.js itself, but it might also be a dummy
  // devbox client which just logs to the console.
  useEffect(() => {
    const configureStripeAnalyticsClient = async () => {
      if (process.env.NODE_ENV === 'development') {
        // If we're running in dev mode, return a client that logs to the console
        // for debugging purposes.
        return createDevBoxClient();
      }

      // Load Analytics.js, but immediately yank the client from the global namespace
      // and capture it here to ensure we can control access to it.
      require('@stripe-internal/analytics-js');

      const stripeAnalyticsClient = window.Analytics || window.Stripelytics;

      delete window.Analytics;
      delete window.Stripelytics;

      // If something goes wrong while loading Analytics.js, just use a stub client.
      // (We don't expect this to happen, but we also don't want to start throwing JS
      // errors in production if somehow it does!)
      if (!stripeAnalyticsClient) {
        return createNoopClient();
      }

      const cookiesEnforcementMode = await cookies.getEnforcementMode();
      const googleAnalyticsAllowed = await cookies.areCategoriesAllowed(
        'advertising',
        'statistics',
      );

      await stripeAnalyticsClient.configure({
        ga: googleAnalyticsAllowed ? {isLoggedIn: !!account.user} : null,
        locale: account.locale.code,
        merchant: account.merchant?.token,
        user: account.user?.token,
        useStripeCookies: true,
        stripeCookiesEnforcementMode: cookiesEnforcementMode,
      });

      return stripeAnalyticsClient;
    };

    configureStripeAnalyticsClient().then((realClient) => {
      // If we've successfully switched to a real client, flush any events that were buffered.
      if (client.flushBufferedEvents) {
        client.flushBufferedEvents(realClient);
      }
      setClient(realClient);
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  return client;
};
